import { useChildScope } from "@/context/ChildScopeContext";

// v65 (Phase 1+) — master-view child indicator.
//
// Renders a compact, coloured row of dots (one per child the entity is
// tagged with) followed by their first names. Designed to fit inline on
// list rows, message bubbles, expense lines, etc. without crowding.
//
// Behaviour:
//  • Returns null when no children are tagged (legacy untagged rows
//    show nothing rather than a misleading "all children" badge).
//  • Returns null when the user has filtered to a single child via
//    the switcher — the badge would just be redundant noise in that
//    view. Pass `forceShow` to override.
//  • Colours come from `child.color` set on the child profile, so the
//    mapping is consistent across the entire app.
//  • Mobile-friendly: dot is 8px (always visible), name uses 11px and
//    truncates after one line.
//  • Up to 3 children render inline; anything beyond shows "+N more".
//
// Per the locked decision, this never says "Space" — it speaks the
// child's actual name. When a row is tagged with multiple children
// (e.g. an event involving Mia & Noah) the badge reads them with an
// ampersand: "Mia & Noah".
export function ChildDots({ childIds = [], variant = "inline", forceShow = false, className = "" }) {
  const { children, selectedChildId } = useChildScope();
  if (!Array.isArray(childIds) || childIds.length === 0) return null;
  // In single-child scope the badge is redundant unless explicitly forced.
  if (!forceShow && selectedChildId) return null;

  const matched = childIds
    .map((id) => children.find((c) => c.id === id))
    .filter(Boolean);
  if (matched.length === 0) return null;

  const visible = matched.slice(0, 3);
  const overflow = matched.length - visible.length;
  const titleText = matched.map((c) => c.name).join(" & ");

  if (variant === "dotsOnly") {
    return (
      <span
        title={titleText}
        aria-label={`Tagged: ${titleText}`}
        className={`inline-flex items-center gap-0.5 ${className}`}
        data-testid="child-dots"
      >
        {visible.map((c) => (
          <span
            key={c.id}
            className="w-2 h-2 rounded-full ring-1 ring-white/60"
            style={{ backgroundColor: c.color || "#1F9FA4" }}
          />
        ))}
        {overflow > 0 && <span className="text-[9px] text-[#6F828A] ml-0.5">+{overflow}</span>}
      </span>
    );
  }

  // Default: dots + names. `text-current` lets the parent control the
  // text colour — required so the badge reads as white on "mine"
  // message bubbles (where the surrounding text is white) and adapts to
  // dark mode without hard-coding a hex.
  const namesLabel = visible.map((c) => c.name.split(" ")[0]).join(" & ")
    + (overflow > 0 ? ` +${overflow}` : "");

  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] leading-none whitespace-nowrap text-current opacity-80 ${className}`}
      title={titleText}
      aria-label={`Tagged: ${titleText}`}
      data-testid="child-dots"
    >
      <span className="inline-flex items-center gap-0.5 flex-shrink-0">
        {visible.map((c) => (
          <span
            key={c.id}
            className="w-2 h-2 rounded-full ring-1 ring-white/60"
            style={{ backgroundColor: c.color || "#1F9FA4" }}
          />
        ))}
      </span>
      <span className="truncate max-w-[120px]">{namesLabel}</span>
    </span>
  );
}
